import { useState, type ReactNode } from "react";
import { API_ROUTES } from "@ziolo/shared";
import { Button } from "@ziolo/ui";
import { apiRequest } from "../../lib/apiClient.js";
import { useFormSubmit } from "../../hooks/useFormSubmit.js";
import { confirmAdmin, useIsAdmin } from "../../hooks/useIsAdmin.js";
import "../../styles/admin.css";

export function AdminPanel({
  title,
  children,
}: {
  title?: string | undefined;
  children: ReactNode;
}) {
  const isAdmin = useIsAdmin();
  const [open, setOpen] = useState(false);
  if (!isAdmin) return null;

  return (
    <details
      className="admin-onpage"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>
        Narzędzia admina
        {title ? ` · ${title}` : ""}
      </summary>
      {open ? <div className="admin-onpage__body">{children}</div> : null}
    </details>
  );
}

export function AdminDeleteButton({
  label = "Usuń",
  confirm,
  disabled,
  onConfirm,
}: {
  label?: string;
  confirm: string;
  disabled?: boolean;
  onConfirm: () => void | Promise<void>;
}) {
  return (
    <Button
      size="sm"
      variant="danger"
      disabled={disabled}
      onClick={() => {
        if (!confirmAdmin(confirm)) return;
        void onConfirm();
      }}
    >
      {label}
    </Button>
  );
}

export function useAdminListTools(options: {
  listPath: string;
  itemPath: (id: number) => string;
  wipeLabel: string;
  onReload: () => Promise<void>;
}) {
  const isAdmin = useIsAdmin();
  const { busy, submit } = useFormSubmit();

  async function remove(id: number) {
    await submit(async () => {
      await apiRequest(options.itemPath(id), { method: "DELETE" });
      await options.onReload();
    }, "Usunięto");
  }

  async function wipe() {
    if (!confirmAdmin(options.wipeLabel)) return;
    await submit(async () => {
      await apiRequest(options.listPath, { method: "DELETE" });
      await options.onReload();
    }, "Wyczyszczono");
  }

  return { isAdmin, busy, remove, wipe };
}

export const ADMIN_LIST = {
  pasjans: {
    listPath: API_ROUTES.admin.pasjansScores,
    itemPath: API_ROUTES.admin.pasjansScore,
    wipeLabel: "Wyczyścić całą tablicę pasjansa?",
  },
  kolo: {
    listPath: API_ROUTES.admin.koloSpins,
    itemPath: API_ROUTES.admin.koloSpin,
    wipeLabel: "Wyczyścić historię koła?",
  },
  sudoku: {
    listPath: API_ROUTES.admin.sudokuScores,
    itemPath: API_ROUTES.admin.sudokuScore,
    wipeLabel: "Wyczyścić tablicę sudoku?",
  },
  game2048: {
    listPath: API_ROUTES.admin.game2048Scores,
    itemPath: API_ROUTES.admin.game2048Score,
    wipeLabel: "Wyczyścić tablicę 2048?",
  },
  bottleClicker: {
    listPath: API_ROUTES.admin.bottleClickerSaves,
    itemPath: API_ROUTES.admin.bottleClickerSave,
    wipeLabel: "Wyczyścić ranking Bottle clickera?",
  },
} as const;
